import { useState, useEffect, useRef, useMemo } from "react";
import {
  CertificateListParams,
  getOrganisationList,
} from "../../services/training-session.service";
import { useDispatch } from "react-redux";
import { showAlert } from "../../store/alertSlice";
import './CertificateModal.scss'
import SearchableSingleSelectDropdown from "../DynamicDropdowns/SearchableSingleSelectDropdown";
import { useDebouncedSearch } from "../../utils/constants/app-constants";
import { mergeSelectedWithList } from "../Hooks/classDetailMultiDropdownHook";
interface CertificateModalProps {
  onSave: (title: string, orgType: DropdownOption) => Promise<void>;
}

export interface DropdownOption {
  id: number;
  name: string;
}

const CertificateModal: React.FC<CertificateModalProps> = ({ onSave }) => {
  const [modalOrgType, setModalOrgType] = useState<DropdownOption | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [orgType, setOrgType] = useState<DropdownOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const modalRef = useRef<HTMLDialogElement>(null);
  const dispatch = useDispatch();
  const [isDataLoading, setIsDataLoading] = useState(true);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const mergedCountryList = useMemo(
      () => mergeSelectedWithList(orgType, modalOrgType, "id"),
      [orgType, modalOrgType]
    );
  
  const fetchDropdownData = async (search = "") => {
    try {
      setIsDataLoading(true);
      const params: CertificateListParams = {
        pageSize: 50,
        name: search,
      };

      const result = await getOrganisationList(params);
      if (!result || result.status !== 200) {
        dispatch(
          showAlert({
            message: "Failed to fetch organisation list",
            type: "error",
          })
        );
      } else {
        const organisations = result?.data?.organisations || [];
      setOrgType(organisations);
    }
  } catch (error) {
    console.error("Error fetching dropdown data:", error);
  } finally {
    setIsDataLoading(false);
  }
};

  const debouncedSearchOrganization = useDebouncedSearch(fetchDropdownData);

  useEffect(() => {
    if (modalOpen) {
      // Give a small delay to ensure DOM is updated
      setTimeout(() => {
        // Find all elements with aria-hidden="true" within our modal
        const ariaHiddenElements = modalRef.current?.querySelectorAll(
          '[aria-hidden="true"]'
        );

        ariaHiddenElements?.forEach((element) => {
          // Check if this element contains any focusable elements
          const focusable = element.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );

          if (focusable.length > 0) {
            // Remove aria-hidden to fix accessibility
            element.removeAttribute("aria-hidden");
            console.log(
              "Removed aria-hidden from element containing focusable content"
            );
          }
        });
      }, 100);
    }
  }, [modalOpen]);

  const handleModalTypeChange = (value: string) => {
    const selectedOption = orgType.find((option) => option.name === value);
    setModalOrgType(selectedOption || null);
  };

  const handleTitleChange = (e: any) => {
    const inputValue =
      e.target?.value || e.detail?.value || e.currentTarget?.value;
    setFileName(inputValue);
  };

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (modalOrgType && fileName) {
      try {
        setIsSubmitting(true);
        await onSave(fileName, modalOrgType);
        closeModal();
      } catch (error) {
        dispatch(
          showAlert({
            message: "Failed to save PDF. Please try again.",
            type: "error",
          })
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const openModal = () => {
    fetchDropdownData();
    toggleModal(true);
  };

  const closeModal = () => {
    toggleModal(false);
  };

  const toggleModal = (isOpen: boolean) => {
    setModalOpen(isOpen);

    if (modalRef.current) {
      modalRef.current.classList.toggle("show", isOpen);
      modalRef.current.style.display = isOpen ? "block" : "none";
      document.body.classList.toggle("modal-open", isOpen);
    }
  };

  return (
    <div>
      {modalOpen === false && (
        <aui-button
          buttontitle="Save Certificate"
          buttonclass="aui-modal-launch"
          size="medium"
          variant="primary"
          data-backdrop="static"
          data-keyboard="false"
          onClick={openModal}
        />
      )}

      <aui-uploaddocumentmodal className="auimodal-sm">
        <dialog
          ref={modalRef}
          className="modal fade auimodal"
          id="certificateModal"
          aria-labelledby="certificateModalLabel"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content d-block p-3 pt-5 mt-2">
              <button
                type="button"
                className="close aui-modalclose position-absolute p-1 aui-modalclosecls"
                onClick={closeModal}
                aria-label="Close"
              >
                <span className="close-icon">×</span>
              </button>

              <div className="modal-header pb-0 pl-0 pt-0 pr-0">
                <aui-modal-headtag className="auimodal-sm">
                  <div id="exampleModalLabel">Save Certificate</div>
                </aui-modal-headtag>
              </div>

              <aui-uploaddocumentmodal-content>
                <div className="modal-body d-block">
                  <form
                    className="w-100 mt-3"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <div>
                      <aui-input
                        inputid="primary-input"
                        label="File name"
                        labelgrid="col-sm-3"
                        inputgrid="col-sm-9"
                        clearable={true}
                        required={true}
                        type="text"
                        value={fileName}
                        onInput={handleTitleChange}
                      />

                      <div className="dropdown-container">
                        <SearchableSingleSelectDropdown
                          listInBtn
                          data={mergedCountryList.map((org) => org.name)}
                          dropdownLabel="Type"
                          placeHolder="Select Organisation Type"
                          mainId="ModalType"
                          isLoading={isDataLoading}
                          onChange={handleModalTypeChange}
                          value={modalOrgType?.name ?? ""}
                          onSearch={debouncedSearchOrganization}
                          dropId="Modal Type"
                        />
                      </div>
                    </div>

                    <div className="auimodals-buttons d-flex w-100 justify-content-center align-items-center mt-4 pt-2">
                      <aui-button
                        size="medium"
                        variant="primary"
                        buttontitle="Submit"
                        buttonclass="aui-modal-button-width aui-modal-btn-trap"
                        disabled={isSubmitting || !fileName || !modalOrgType}
                        onClick={handleSubmit}
                      />
                    </div>
                  </form>
                </div>
              </aui-uploaddocumentmodal-content>
            </div>
          </div>
        </dialog>
      </aui-uploaddocumentmodal>
    </div>
  );
};

export default CertificateModal;
